/**
 * Audit room-timing + run-lifecycle analytics — a small module-level
 * singleton (not React state) so both `index.jsx` (room enter/exit) and
 * `ResultsRoom.jsx` (final `audit_completed` totals) share the exact
 * same running counters without prop-drilling through AuditContext.
 *
 * `run_id` here is a LOCAL, analytics-only correlation id — distinct
 * from the backend's own `audit_id` (which stays null until a report is
 * actually captured). Never sent to the backend, never persisted.
 */
import { capture, captureOaiqEvent, getAttributionContext } from "../../lib/analytics";

let runId = null;
let startedAtMs = null;
let totalActiveMs = 0;
let roomsVisited = null;
let roomsCompleted = null;
let current = null; // { roomId, roomNumber, accumulatedMs, visibleSinceMs, isVisible }
let lifecycleAttached = false;

const nowMs = () => Date.now();

function attachLifecycleListeners() {
  if (lifecycleAttached || typeof document === "undefined") return;
  lifecycleAttached = true;
  // Pause/resume active-time accrual on tab visibility — active duration
  // must never count hidden-tab/backgrounded time.
  document.addEventListener("visibilitychange", () => {
    if (!current) return;
    if (document.hidden) {
      if (current.isVisible) {
        current.accumulatedMs += nowMs() - current.visibleSinceMs;
        current.isVisible = false;
      }
    } else if (!current.isVisible) {
      current.visibleSinceMs = nowMs();
      current.isVisible = true;
    }
  });
  // Catch-all for a hard tab close/reload while a room is still open —
  // React's own unmount cleanup never runs in that case.
  window.addEventListener("pagehide", () => exitRoom("page_exit"));
}

/** Starts (or restarts, e.g. "Restart Full Audit") a fresh Audit run. */
export function startAuditRun(entryPath) {
  attachLifecycleListeners();
  runId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(nowMs());
  startedAtMs = nowMs();
  totalActiveMs = 0;
  roomsVisited = new Set();
  roomsCompleted = new Set();
  current = null;
  const attribution = getAttributionContext() || {};
  capture("audit_started", {
    run_id: runId,
    audit_id: null,
    contact_id: null,
    entry_path: entryPath || (typeof window !== "undefined" ? window.location.pathname : null),
    utm_source: attribution.utm_source || null,
    utm_medium: attribution.utm_medium || null,
    utm_campaign: attribution.utm_campaign || null,
    utm_content: attribution.utm_content || null,
    utm_term: attribution.utm_term || null,
    gclid: attribution.gclid || null,
    link_id: attribution.link_id || null,
    landing_page: attribution.landing_page || null,
    referrer: attribution.referrer || null,
  });
  captureOaiqEvent("audit_started");
  return runId;
}

/** Marks a room as entered — always pair with a later `exitRoom()`. */
export function enterRoom(roomId, roomNumber) {
  attachLifecycleListeners();
  current = {
    roomId,
    roomNumber,
    accumulatedMs: 0,
    visibleSinceMs: nowMs(),
    isVisible: typeof document === "undefined" || !document.hidden,
  };
  if (roomsVisited) roomsVisited.add(roomId);
}

function computeActiveMs() {
  if (!current) return 0;
  let ms = current.accumulatedMs;
  if (current.isVisible) ms += nowMs() - current.visibleSinceMs;
  return Math.max(0, Math.round(ms));
}

/**
 * Fires `audit_room_exited` for whatever room is currently open. A no-op
 * (never double-fires) if no room is open — e.g. a second call for a
 * room already exited, or the "explicit_exit" unmount-cleanup fallback
 * running after an explicit exit already closed it.
 */
export function exitRoom(exitReason) {
  if (!current) return null;
  const activeMs = computeActiveMs();
  totalActiveMs += activeMs;
  const exited = current;
  if ((exitReason === "next_room" || exitReason === "audit_completed") && roomsCompleted) {
    roomsCompleted.add(exited.roomId);
  }
  capture("audit_room_exited", {
    run_id: runId,
    room_id: exited.roomId,
    room_number: exited.roomNumber,
    room_active_duration_ms: activeMs,
    exit_reason: exitReason,
  });
  current = null;
  return { roomId: exited.roomId, activeMs };
}

export const hasOpenRoom = () => current !== null;

const COMPLETED_EVENT_GUARD_KEY = "iag_audit_completed_event_run_id_v1";

/**
 * De-duplication guard for the one-time `audit_completed` conversion
 * (PostHog + ChatGPT Ads) — returns `true` the FIRST time it's called for
 * a given `run_id` (the caller should go ahead and fire the completion
 * events), and `false` on every subsequent call for the SAME `run_id`
 * (already fired — the caller must skip firing again). sessionStorage-
 * backed so this survives a hard page refresh within the same tab, not
 * just a React re-render/remount — covers the "concurrent poll-tick
 * race" and "refresh after completion" duplicate scenarios without any
 * new persistence architecture (reuses the same sessionStorage mechanism
 * already used elsewhere in this module/ResultsRoom.jsx).
 */
export function claimAuditCompletedEvent(completionRunId) {
  if (!completionRunId) return true; // no identity to dedupe on — fire rather than silently drop a genuine conversion
  try {
    if (sessionStorage.getItem(COMPLETED_EVENT_GUARD_KEY) === completionRunId) return false;
    sessionStorage.setItem(COMPLETED_EVENT_GUARD_KEY, completionRunId);
    return true;
  } catch {
    return true;
  }
}

/** Read at `audit_completed` time — includes the still-open room's
 * in-progress active time without requiring a separate exit call. */
export function getAuditTotals() {
  const inProgress = current ? computeActiveMs() : 0;
  return {
    run_id: runId,
    audit_active_duration_ms: totalActiveMs + inProgress,
    audit_elapsed_duration_ms: startedAtMs ? nowMs() - startedAtMs : null,
    rooms_visited_count: roomsVisited ? roomsVisited.size : 0,
    rooms_completed_count: roomsCompleted ? roomsCompleted.size : 0,
  };
}
